import React from 'react';
import { useApiData } from '../../api/client';
import type { DastarkhanProject } from '../../api/dastarkhan';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Card,
  Button,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface ProjectListProps {
  onSelectProject: (projectId: string) => void;
  onCreateProject: () => void;
}

export const ProjectList: React.FC<ProjectListProps> = ({ onSelectProject, onCreateProject }) => {
  const { theme } = useTheme();
  const { isAdmin } = usePermissions();
  const { data: projects, loading, error, refetch } = useApiData<DastarkhanProject[]>('/dastarkhan/projects');

  return (
    <Stack gap="md">
      <Flex justify="between" align="center">
        <h2 style={{ margin: 0, fontSize: '1.2rem', color: theme.colors.text.primary }}>
          Investigation Projects
        </h2>
        {isAdmin && (
          <Button variant="solid" color="primary" size="sm" onClick={onCreateProject}>
            Create Project
          </Button>
        )}
      </Flex>

      {error && (
        <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>
          {error.message}
        </div>
      )}

      {loading ? (
        <p style={{ color: theme.colors.text.secondary }}>Loading projects...</p>
      ) : !projects || projects.length === 0 ? (
        <Card>
          <Card.Body>
            <p style={{ margin: 0, color: theme.colors.text.secondary }}>
              No projects found. {isAdmin ? 'Create one to get started.' : 'Ask an admin to add you to a project.'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: spacing.md }}>
          {projects.map((project) => (
            <Card
              key={project.id}
              variant="elevated"
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectProject(project.id)}
            >
              <Card.Body>
                <Stack gap="sm">
                  <Flex justify="between" align="start">
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1rem', color: theme.colors.text.primary }}>
                        {project.name}
                      </h3>
                      {project.matter_code && (
                        <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                          {project.matter_code}
                        </span>
                      )}
                    </div>
                    <Flex gap="xs">
                      <span style={{
                        padding: `2px ${spacing.xs}`,
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: project.status === 'active'
                          ? theme.colors.semantic.success.light
                          : theme.colors.background.paper,
                        color: project.status === 'active'
                          ? theme.colors.semantic.success.default
                          : theme.colors.text.secondary,
                      }}>
                        {project.status}
                      </span>
                      {project.has_credentials && (
                        <span style={{
                          padding: `2px ${spacing.xs}`,
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          background: project.neo4j_connected
                            ? theme.colors.semantic.success.light
                            : theme.colors.semantic.warning.light,
                          color: project.neo4j_connected
                            ? theme.colors.semantic.success.default
                            : theme.colors.semantic.warning.default,
                        }}>
                          {project.neo4j_connected ? 'Neo4j Connected' : 'Neo4j Configured'}
                        </span>
                      )}
                    </Flex>
                  </Flex>
                  {project.description && (
                    <p style={{
                      margin: 0,
                      fontSize: '0.85rem',
                      color: theme.colors.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {project.description}
                    </p>
                  )}
                </Stack>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </Stack>
  );
};
